
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { deleteTeacher } from '@/services/db/teachers';
import { useTeacherData } from '@/hooks/useTeacherData';

interface TeacherCardActionsProps {
  teacherId: string;
  teacherName: string;
  onDeleteSuccess?: () => void;
}

export const TeacherCardActions = ({ teacherId, teacherName, onDeleteSuccess }: TeacherCardActionsProps) => {
  const navigate = useNavigate();

  const handleView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/teachers/${teacherId}`);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/teachers/edit/${teacherId}`);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm(`Are you sure you want to delete ${teacherName}?`)) {
      try {
        await deleteTeacher(teacherId);
        toast.success(`${teacherName} has been deleted successfully.`);
        
        // Call the callback if provided
        if (onDeleteSuccess) {
          onDeleteSuccess();
        } else {
          // Force refresh after deletion if no callback provided
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } catch (error) {
        console.error('Error deleting teacher:', error);
        toast.error('Failed to delete teacher.');
      }
    }
  };

  return (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-50 bg-white">
          <DropdownMenuItem onClick={handleView}>
            <Eye className="mr-2 h-4 w-4" />
            <span>View Details</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            <span>Edit</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDelete} className="text-red-600">
            <Trash2 className="mr-2 h-4 w-4" />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
